import React, { useState, useEffect } from 'react';
import { useSocket } from '../../context/SocketContext';
import { socketService } from '../../services/socketService';
import { apiService } from '../../services/api';
import { useConfirm } from '../confirm/ConfirmDialogProvider';
import { notifyWarning } from '../../utils/notificationCenter';
import LoadingState from '../common/LoadingState';
import { 
  PlayIcon, 
  PauseIcon, 
  StopIcon, 
  PlusIcon,
  ClockIcon,
  UsersIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { BarChart3, CheckCircle2, Circle, Clock, FileText, Pencil, XCircle } from 'lucide-react';

interface VotingSessionManagerProps {
  assemblyId: string;
  userRole: 'admin' | 'manager' | 'owner' | 'tenant' | 'guest';
  assemblyStatus?: 'draft' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
}

interface NewVotingSession {
  agendaItemId: string;
  question: string;
  description: string;
  options: string[];
  type: 'yes_no' | 'yes_no_abstain' | 'multiple_choice';
  durationMinutes: number;
  isSecret: boolean;
}

const shellCardClassName = 'rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_-20px_rgba(15,23,42,0.45)]';

/** Alineado con CreateVotingSessionDto / UpdateVotingSessionDto */
const QUESTION_MIN = 3;
const QUESTION_MAX = 500;
const DESCRIPTION_MIN = 10;
const DESCRIPTION_MAX = 2000;
const CHAR_WARN_RATIO = 0.9;

type VotingFormErrors = {
  description?: string;
  question?: string;
  agendaItemId?: string;
  options?: string;
};

const counterClassName = (length: number, max: number) => {
  if (length > max) return 'text-red-600 font-medium';
  if (length >= Math.floor(max * CHAR_WARN_RATIO)) return 'text-amber-600';
  return 'text-slate-400';
};

const validateVotingForm = (session: NewVotingSession): VotingFormErrors => {
  const errors: VotingFormErrors = {};
  const question = session.question.trim();
  const description = session.description.trim();

  if (!session.agendaItemId) {
    errors.agendaItemId = 'Selecciona un punto de agenda';
  }

  if (!question) {
    errors.question = 'La pregunta es obligatoria';
  } else if (question.length < QUESTION_MIN) {
    errors.question = `La pregunta debe tener al menos ${QUESTION_MIN} caracteres`;
  } else if (session.question.length > QUESTION_MAX) {
    errors.question = `La pregunta no puede superar ${QUESTION_MAX} caracteres (tienes ${session.question.length})`;
  }

  if (!description) {
    // Descripción opcional: vacía está bien
  } else if (description.length < DESCRIPTION_MIN) {
    errors.description = `Si agregas descripción, debe tener al menos ${DESCRIPTION_MIN} caracteres`;
  } else if (session.description.length > DESCRIPTION_MAX) {
    errors.description = `La descripción no puede superar ${DESCRIPTION_MAX} caracteres (tienes ${session.description.length})`;
  }

  if (session.options.length < 2 || session.options.some((o) => !o.trim())) {
    errors.options = 'Debes ingresar al menos 2 opciones válidas';
  }

  return errors;
};

const mapApiValidationErrors = (error: unknown): VotingFormErrors | undefined => {
  const responseErrors = (error as { response?: { data?: { errors?: Array<{ field?: string; constraints?: Record<string, string> }> } } })
    ?.response?.data?.errors;
  if (!Array.isArray(responseErrors) || responseErrors.length === 0) {
    return undefined;
  }

  const mapped: VotingFormErrors = {};
  for (const entry of responseErrors) {
    const field = entry.field as keyof VotingFormErrors | undefined;
    const message = entry.constraints
      ? Object.values(entry.constraints)[0]
      : undefined;
    if (field && message && ['question', 'description', 'agendaItemId', 'options'].includes(field)) {
      mapped[field] = message;
    }
  }
  return Object.keys(mapped).length > 0 ? mapped : undefined;
};

const MIN_LOADING_MS = 250;

const ensureMinimumLoadingTime = async (startTime: number) => {
  const elapsed = Date.now() - startTime;
  if (elapsed < MIN_LOADING_MS) {
    await new Promise((resolve) => setTimeout(resolve, MIN_LOADING_MS - elapsed));
  }
};

const VotingSessionManager: React.FC<VotingSessionManagerProps> = ({ 
  assemblyId, 
  userRole,
  assemblyStatus
}) => {
  const { activeVotingSession, isConnected } = useSocket();
  const { confirm } = useConfirm();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSession, setEditingSession] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAgendaItems, setIsLoadingAgendaItems] = useState(true);
  const [isLoadingVotingSessions, setIsLoadingVotingSessions] = useState(true);
  const [agendaItems, setAgendaItems] = useState<any[]>([]);
  const [votingSessions, setVotingSessions] = useState<any[]>([]);
  const [newSession, setNewSession] = useState<NewVotingSession>({
    agendaItemId: '',
    question: '',
    description: '',
    options: ['Sí', 'No'],
    type: 'yes_no',
    durationMinutes: 5,
    isSecret: false
  });
  const [formErrors, setFormErrors] = useState<VotingFormErrors>();
  const isAssemblyActive = assemblyStatus === 'in_progress';
  const canCreateVotingDraft = assemblyStatus === 'scheduled' || assemblyStatus === 'in_progress';
  const isLoadingInitialData = isLoadingAgendaItems || isLoadingVotingSessions;
  const questionLen = newSession.question.length;
  const descriptionLen = newSession.description.length;
  const hasLengthErrors =
    questionLen > QUESTION_MAX ||
    descriptionLen > DESCRIPTION_MAX ||
    (descriptionLen > 0 && descriptionLen < DESCRIPTION_MIN) ||
    (questionLen > 0 && questionLen < QUESTION_MIN);

  // Cargar agenda items al montar el componente
  useEffect(() => {
    const loadAgendaItems = async () => {
      if (!assemblyId) {
        setIsLoadingAgendaItems(false);
        return;
      }

      const startTime = Date.now();
      setIsLoadingAgendaItems(true);
      try {
        const response = await apiService.getAgendaItemsVotingView(assemblyId);
        const items = response.data.data || response.data;
        const votingItems = Array.isArray(items) ? items : [];
        setAgendaItems(votingItems);
        
        // Seleccionar el primer agenda item por defecto
        if (votingItems && votingItems.length > 0) {
          setNewSession(prev => ({
            ...prev,
            agendaItemId: votingItems[0].id
          }));
        }
      } catch (error) {
        console.error('Error al cargar agenda items:', error);
      } finally {
        await ensureMinimumLoadingTime(startTime);
        setIsLoadingAgendaItems(false);
      }
    };

    loadAgendaItems();
  }, [assemblyId]);

  // Función separada para recargar agenda items
  const reloadAgendaItems = async () => {
    try {
      const response = await apiService.getAgendaItemsVotingView(assemblyId);
      const items = response.data.data || response.data;
      setAgendaItems(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error('Error al recargar agenda items:', error);
    }
  };

  // ✅ OPTIMIZACIÓN: Cargar sesiones SOLO una vez al inicio
  // Las actualizaciones en tiempo real vienen por Socket.IO a través de activeVotingSession
  useEffect(() => {
    const loadVotingSessions = async () => {
      const startTime = Date.now();
      setIsLoadingVotingSessions(true);
      try {
        console.log('� Carga inicial de sesiones de votación para assemblyId:', assemblyId);
        const response = await apiService.getVotingSessionsManagerView(assemblyId);
        console.log('📡 Respuesta del API:', response.data);
        
        // Manejar tanto si la respuesta tiene .sessions como si es un array directo
        // La respuesta real es: { success: true, data: Array(5), pagination: {...} }
        const sessionsData = response.data.data || response.data.sessions || response.data;
        console.log('📋 Sesiones procesadas:', sessionsData);
        
        // ✅ Ordenar sesiones: nuevas primero (por fecha de creación descendente)
        const sortedSessions = Array.isArray(sessionsData) 
          ? sessionsData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          : [];
        
        setVotingSessions(sortedSessions);
        console.log('✅ Sesiones establecidas en el estado (ordenadas: nuevas primero)');
      } catch (error) {
        console.error('❌ Error al cargar sesiones de votación:', error);
      } finally {
        await ensureMinimumLoadingTime(startTime);
        setIsLoadingVotingSessions(false);
      }
    };

    // Listener para recargar agenda (estos eventos no vienen por Socket.IO)
    const handleAgendaEvents = () => {
      console.log('🔄 Recargando agenda por evento de agenda');
      reloadAgendaItems();
    };

    // Registrar listeners solo para eventos de agenda
    window.addEventListener('agenda-item-created', handleAgendaEvents);
    window.addEventListener('agenda-item-updated', handleAgendaEvents);
    window.addEventListener('agenda-item-deleted', handleAgendaEvents);

    if (assemblyId) {
      loadVotingSessions();
    } else {
      setIsLoadingVotingSessions(false);
    }

    return () => {
      window.removeEventListener('agenda-item-created', handleAgendaEvents);
      window.removeEventListener('agenda-item-updated', handleAgendaEvents);
      window.removeEventListener('agenda-item-deleted', handleAgendaEvents);
    };
  }, [assemblyId]); // ✅ Solo se ejecuta cuando cambia assemblyId

  // ✅ OPTIMIZACIÓN: Actualizar lista local cuando llega una sesión por Socket.IO
  // Agregar nuevas sesiones al INICIO de la lista (nuevas primero)
  useEffect(() => {
    if (activeVotingSession) {
      console.log('🔄 Sesión actualizada por Socket.IO:', activeVotingSession.id, activeVotingSession.status);
      
      setVotingSessions(prev => {
        const index = prev.findIndex(s => s.id === activeVotingSession.id);
        if (index >= 0) {
          // Actualizar sesión existente en su lugar
          const updated = [...prev];
          updated[index] = activeVotingSession;
          return updated;
        } else {
          // ✅ Agregar nueva sesión al PRINCIPIO (nuevas primero)
          return [activeVotingSession, ...prev];
        }
      });
    }
  }, [activeVotingSession]); // ✅ Solo reacciona a cambios del Socket.IO, sin HTTP requests

  // ✅ Escuchar eventos de actualización de votación en tiempo real
  useEffect(() => {
    const handleVotingSessionUpdated = (data: any) => {
      console.log('🔄 [VotingSessionManager] voting:session-updated recibido COMPLETO:', JSON.stringify(data, null, 2));
      console.log('🔄 [VotingSessionManager] data.votingSession:', data?.votingSession);
      console.log('🔄 [VotingSessionManager] data.id:', data?.id);
      
      const updatedSession = data?.votingSession || data;
      console.log('🔄 [VotingSessionManager] updatedSession extraída:', updatedSession);
      console.log('🔄 [VotingSessionManager] updatedSession.id:', updatedSession?.id);
      
      if (!updatedSession?.id) {
        console.warn('⚠️ [VotingSessionManager] updatedSession no tiene ID válido');
        return;
      }
      
      setVotingSessions(prev => {
        console.log('🔄 [VotingSessionManager] Buscando sesión con id:', updatedSession.id, 'en:', prev.map(s => s.id));
        const index = prev.findIndex(s => s.id === updatedSession.id);
        console.log('🔄 [VotingSessionManager] Índice encontrado:', index);
        
        if (index >= 0) {
          // Actualizar sesión existente
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            ...updatedSession
          };
          console.log('✅ [VotingSessionManager] Sesión actualizada en índice:', index);
          return updated;
        } else {
          console.warn('⚠️ [VotingSessionManager] No se encontró sesión con id:', updatedSession.id);
        }
        return prev;
      });
    };

    console.log('📌 [VotingSessionManager] Registrando listener para voting:session-updated');
    socketService.on('voting:session-updated', handleVotingSessionUpdated);

    return () => {
      console.log('📌 [VotingSessionManager] Desregistrando listener para voting:session-updated');
      socketService.off('voting:session-updated', handleVotingSessionUpdated);
    };
  }, []);

  const handleCreateSession = async () => {
    if (!canCreateVotingDraft) {
      notifyWarning('Solo puedes crear votaciones cuando la asamblea está programada o en curso.');
      return;
    }

    const errors = validateVotingForm(newSession);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsLoading(true);
    try {
      const votingData = {
        agendaItemId: newSession.agendaItemId,
        question: newSession.question.trim(),
        description: newSession.description.trim() || undefined,
        type: newSession.type,
        options: newSession.options,
        durationMinutes: newSession.durationMinutes,
        isSecret: newSession.isSecret
      };

      const response = await apiService.createVotingSession(votingData);
      console.log('✅ Sesión de votación creada:', response.data);

      // Actualización optimista local para no depender del timing de Socket.IO.
      const createdSession = response.data?.data;
      if (createdSession?.id) {
        setVotingSessions((prev) => {
          const exists = prev.some((session) => session.id === createdSession.id);
          if (exists) return prev;
          return [createdSession, ...prev];
        });
      }
      
      // Resetear formulario
      setNewSession({
        agendaItemId: agendaItems.length > 0 ? agendaItems[0].id : '',
        question: '',
        description: '',
        options: ['Sí', 'No'],
        type: 'yes_no',
        durationMinutes: 5,
        isSecret: false
      });
      
      setShowCreateForm(false);
      setFormErrors(undefined);
    } catch (error) {
      console.error('Error al crear sesión de votación:', error);
      const apiErrors = mapApiValidationErrors(error);
      if (apiErrors) {
        setFormErrors(apiErrors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePauseSession = async () => {
    if (!activeVotingSession) return;
    
    setIsLoading(true);
    try {
      console.log('Pausando sesión de votación:', activeVotingSession.id);
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error al pausar sesión:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResumeSession = async () => {
    if (!activeVotingSession) return;
    
    setIsLoading(true);
    try {
      console.log('Reanudando sesión de votación:', activeVotingSession.id);
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error al reanudar sesión:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndSession = async () => {
    if (!activeVotingSession) return;
    
    const shouldEnd = await confirm({
      title: 'Finalizar sesión de votación',
      message: '¿Estás seguro de que quieres finalizar esta sesión de votación?',
      confirmText: 'Finalizar',
      cancelText: 'Cancelar',
      variant: 'warning',
    });

    if (!shouldEnd) {
      return;
    }
    
    setIsLoading(true);
    try {
      console.log('Finalizando sesión de votación:', activeVotingSession.id);
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error al finalizar sesión:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartSession = async (sessionId: string) => {
    if (!isAssemblyActive) {
      notifyWarning('Solo puedes iniciar votaciones cuando la asamblea está en curso.');
      return;
    }

    const shouldStart = await confirm({
      title: 'Iniciar votación',
      message: '¿Estás seguro de que quieres iniciar esta votación?',
      confirmText: 'Iniciar',
      cancelText: 'Cancelar',
      variant: 'info',
    });

    if (!shouldStart) {
      return;
    }
    
    setIsLoading(true);
    try {
      console.log('🚀 Iniciando sesión de votación:', sessionId);
      await apiService.startVoting(sessionId);
      
      // ✅ OPTIMIZACIÓN: No recargar sesiones manualmente - Socket.IO lo hará automáticamente
      // El backend emite 'voting:session-started' que actualiza activeVotingSession en SocketContext
      
      console.log('✅ Sesión iniciada exitosamente');
      
      // Notificar a otros componentes que se inició una votación (para casos edge)
      window.dispatchEvent(new CustomEvent('voting-session-started', { 
        detail: { sessionId, assemblyId } 
      }));
      
    } catch (error) {
      console.error('❌ Error al iniciar sesión:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSession = (session: any) => {
    setEditingSession(session);
    setFormErrors(undefined);
    setNewSession({
      agendaItemId: session.agendaItemId,
      question: session.question,
      description: session.description || '',
      options: session.options.map((opt: any) => opt.text),
      type: session.type,
      durationMinutes: Math.floor(session.duration / 60),
      isSecret: session.isAnonymous
    });
    setShowCreateForm(true);
  };

  const handleUpdateSession = async () => {
    if (!editingSession) return;
    const errors = validateVotingForm(newSession);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsLoading(true);
    try {
      const votingData = {
        question: newSession.question.trim(),
        description: newSession.description.trim() || undefined,
        type: newSession.type,
        options: newSession.options,
        durationMinutes: newSession.durationMinutes,
        isSecret: newSession.isSecret
      };

      await apiService.updateVotingSession(editingSession.id, votingData);
      console.log('✅ Sesión de votación actualizada:', editingSession.id);
      
      // ✅ OPTIMIZACIÓN: No recargar sesiones manualmente - Socket.IO lo hará automáticamente
      // El backend debe emitir evento de actualización que se maneje en SocketContext
      
      // Resetear formulario
      setEditingSession(null);
      setNewSession({
        agendaItemId: agendaItems.length > 0 ? agendaItems[0].id : '',
        question: '',
        description: '',
        options: ['Sí', 'No'],
        type: 'yes_no',
        durationMinutes: 5,
        isSecret: false
      });
      
      setShowCreateForm(false);
      setFormErrors(undefined);
    } catch (error) {
      console.error('Error al actualizar sesión de votación:', error);
      const apiErrors = mapApiValidationErrors(error);
      if (apiErrors) {
        setFormErrors(apiErrors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId: string, sessionStatus: string) => {
    // Solo permitir eliminar si no está activa
    if (sessionStatus === 'active') {
      console.error('No se puede eliminar una votación activa. Debe terminarla primero.');
      return;
    }
    
    const confirmText = '¿Estás seguro de que quieres eliminar esta votación? Esta acción no se puede deshacer.';
    
    const shouldDelete = await confirm({
      title: 'Eliminar votación',
      message: confirmText,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });

    if (!shouldDelete) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.deleteVotingSession(sessionId);
      console.log(`✅ Sesión de votación ${response.data.action}:`, sessionId);
      
      // ✅ OPTIMIZACIÓN: Remover sesión del estado local inmediatamente
      // No necesitamos recargar todas las sesiones desde el servidor
      setVotingSessions(prev => prev.filter(s => s.id !== sessionId));
      
      console.log(`✅ Sesión ${response.data.action} exitosamente`);
    } catch (error) {
      console.error('Error al eliminar sesión:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndVotingSession = async (sessionId: string) => {
    const confirmText = '¿Estás seguro de que quieres terminar esta votación? Los votos ya emitidos se conservarán y no se podrán emitir más votos.';
    
    const shouldEnd = await confirm({
      title: 'Terminar votación',
      message: confirmText,
      confirmText: 'Terminar',
      cancelText: 'Cancelar',
      variant: 'warning',
    });

    if (!shouldEnd) {
      return;
    }

    setIsLoading(true);
    try {
      await apiService.endVoting(sessionId);
      console.log('✅ Sesión de votación terminada:', sessionId);
      
      // ✅ OPTIMIZACIÓN: No recargar sesiones manualmente - Socket.IO lo hará automáticamente
      // El backend emite 'voting:session-ended' que actualiza activeVotingSession en SocketContext
      
      console.log('✅ Sesión terminada exitosamente');
    } catch (error) {
      console.error('❌ Error al terminar sesión:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingSession(null);
    setShowCreateForm(false);
    setNewSession({
      agendaItemId: agendaItems.length > 0 ? agendaItems[0].id : '',
      question: '',
      description: '',
      options: ['Sí', 'No'],
      type: 'yes_no',
      durationMinutes: 5,
      isSecret: false
    });
    setFormErrors(undefined);
  };

  const addOption = () => {
    setNewSession(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const removeOption = (index: number) => {
    if (newSession.options.length > 2) {
      setNewSession(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index)
      }));
    }
  };

  const updateOption = (index: number, value: string) => {
    setNewSession(prev => ({
      ...prev,
      options: prev.options.map((option, i) => i === index ? value : option)
    }));
  };

  // Log de debug para ver el estado actual
  console.log('🎯 VotingSessionManager render:', {
    userRole,
    assemblyId,
    votingSessionsCount: votingSessions.length,
    votingSessions,
    activeVotingSession
  });

  if (userRole !== 'admin') {
    return (
      <div className={`${shellCardClassName} bg-slate-50 p-6`}>
        <div className="text-center">
          <UsersIcon className="mx-auto mb-4 h-12 w-12 text-slate-400" />
          <h3 className="mb-2 text-lg font-medium text-slate-900">
            Acceso Restringido
          </h3>
          <p className="text-slate-600">
            Solo los administradores pueden gestionar sesiones de votación.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Create New Session */}
      <div className={`${shellCardClassName} p-6`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            {editingSession ? 'Editar Votación' : 'Gestión de Votaciones'}
          </h3>
          {!showCreateForm && (
            <button
              onClick={() => canCreateVotingDraft && setShowCreateForm(true)}
              className={`flex items-center rounded-xl px-4 py-2 text-sm font-medium text-white transition-colors ${canCreateVotingDraft ? 'bg-blue-600 hover:bg-blue-700' : 'cursor-not-allowed bg-slate-400'}`}
              disabled={!canCreateVotingDraft}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Nueva Votación
            </button>
          )}
        </div>

        {assemblyStatus === 'scheduled' && (
          <div className="mb-4 rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            Puedes crear votaciones en borrador desde ahora. Solo podrás iniciarlas cuando la asamblea esté en curso.
          </div>
        )}

        {!canCreateVotingDraft && (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Solo se pueden crear votaciones cuando la asamblea está programada o en curso.
          </div>
        )}

        {showCreateForm ? (
          <div className="space-y-4">
            {/* Agenda Item Selector */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Punto de Agenda *
              </label>
              <select
                value={newSession.agendaItemId}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewSession(prev => ({ ...prev, agendaItemId: value }));
                  setFormErrors(prev => ({
                    ...(prev || {}),
                    agendaItemId: value ? undefined : 'Selecciona un punto de agenda',
                  }));
                }}
                className={`w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors?.agendaItemId ? 'border-red-300' : 'border-slate-300'}`}
                required
              >
                <option value="">Seleccionar punto de agenda</option>
                {agendaItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.order}. {item.title}
                  </option>
                ))}
              </select>
              {formErrors?.agendaItemId && (
                <p className="mt-1 text-sm text-red-600">{formErrors.agendaItemId}</p>
              )}
            </div>

            {/* Question */}
            <div>
              <div className="mb-1 flex items-end justify-between gap-3">
                <label className="block text-sm font-medium text-slate-700">
                  Pregunta *
                </label>
                <span className={`text-xs tabular-nums ${counterClassName(questionLen, QUESTION_MAX)}`}>
                  {questionLen} / {QUESTION_MAX}
                </span>
              </div>
              <input
                type="text"
                value={newSession.question}
                maxLength={QUESTION_MAX}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewSession(prev => ({ ...prev, question: value }));
                  const trimmed = value.trim();
                  let questionError: string | undefined;
                  if (!trimmed) questionError = 'La pregunta es obligatoria';
                  else if (trimmed.length < QUESTION_MIN) {
                    questionError = `La pregunta debe tener al menos ${QUESTION_MIN} caracteres`;
                  } else if (value.length > QUESTION_MAX) {
                    questionError = `La pregunta no puede superar ${QUESTION_MAX} caracteres (tienes ${value.length})`;
                  }
                  setFormErrors(prev => ({ ...(prev || {}), question: questionError }));
                }}
                className={`w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors?.question ? 'border-red-300' : 'border-slate-300'}`}
                placeholder="Ej: ¿Aprueba la mesa de trabajo con Constructora Bolívar?"
              />
              {formErrors?.question && (
                <p className="mt-1 text-sm text-red-600">{formErrors.question}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <div className="mb-1 flex items-end justify-between gap-3">
                <label className="block text-sm font-medium text-slate-700">
                  Descripción (opcional, para el acta)
                </label>
                <span className={`text-xs tabular-nums ${counterClassName(descriptionLen, DESCRIPTION_MAX)}`}>
                  {descriptionLen} / {DESCRIPTION_MAX}
                </span>
              </div>
              <textarea
                value={newSession.description}
                maxLength={DESCRIPTION_MAX}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewSession(prev => ({ ...prev, description: value }));
                  const trimmed = value.trim();
                  let descriptionError: string | undefined;
                  if (trimmed && trimmed.length < DESCRIPTION_MIN) {
                    descriptionError = `Si agregas descripción, debe tener al menos ${DESCRIPTION_MIN} caracteres`;
                  } else if (value.length > DESCRIPTION_MAX) {
                    descriptionError = `La descripción no puede superar ${DESCRIPTION_MAX} caracteres (tienes ${value.length})`;
                  }
                  setFormErrors(prev => ({ ...(prev || {}), description: descriptionError }));
                }}
                rows={5}
                className={`w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors?.description ? 'border-red-300' : 'border-slate-300'}`}
                placeholder="Texto completo de la pregunta si el título es corto (opcional)"
              />
              {formErrors?.description && (
                <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
              )}
            </div>

            {/* Options */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Opciones de Votación *
              </label>
              <div className="space-y-2">
                {newSession.options.map((option, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        updateOption(index, e.target.value);
                        setFormErrors(prev => ({ ...(prev || {}), options: undefined }));
                      }}
                      className={`flex-1 rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors?.options ? 'border-red-300' : 'border-slate-300'}`}
                      placeholder={`Opción ${index + 1}`}
                    />
                    {newSession.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="rounded-lg px-2 py-1 text-sm text-red-600 transition-colors hover:bg-red-50 hover:text-red-800"
                        aria-label={`Eliminar opción ${index + 1}`}
                      >
                        Quitar
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {formErrors?.options && (
                <p className="mt-1 text-sm text-red-600">{formErrors.options}</p>
              )}
              <button
                type="button"
                onClick={addOption}
                className="mt-2 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800"
              >
                + Agregar opción
              </button>
            </div>

            {/* Settings Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Tipo de Votación
                </label>
                <select
                  value={newSession.type}
                  onChange={(e) => setNewSession(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="yes_no">Sí/No</option>
                  <option value="yes_no_abstain">Sí/No/Abstención</option>
                  <option value="multiple_choice">Opción Múltiple</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Duración (minutos)
                </label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={newSession.durationMinutes}
                  onChange={(e) => setNewSession(prev => ({ ...prev, durationMinutes: parseInt(e.target.value) }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Settings Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isSecret"
                  checked={newSession.isSecret}
                  onChange={(e) => setNewSession(prev => ({ ...prev, isSecret: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="isSecret" className="ml-2 text-sm text-slate-700">
                  Votación secreta (oculta quién votó qué)
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={editingSession ? handleCancelEdit : () => setShowCreateForm(false)}
                className="rounded-xl border border-slate-300 px-4 py-2 text-slate-600 transition-colors hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={editingSession ? handleUpdateSession : handleCreateSession}
                disabled={
                  isLoading ||
                  hasLengthErrors ||
                  !!formErrors?.question ||
                  !!formErrors?.description ||
                  !!formErrors?.agendaItemId ||
                  !!formErrors?.options ||
                  !newSession.agendaItemId ||
                  !newSession.question.trim() ||
                  newSession.question.trim().length < QUESTION_MIN ||
                  newSession.options.some(opt => !opt.trim())
                }
                className="rounded-xl bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {editingSession ? 'Actualizando...' : 'Creando...'}
                  </div>
                ) : (
                  editingSession ? 'Actualizar Votación' : 'Crear Votación'
                )}
              </button>
            </div>
          </div>
        ) : isLoadingInitialData ? (
          <div className="text-center py-8">
            <LoadingState />
          </div>
        ) : votingSessions.length > 0 ? (
          // ✅ Mostrar SIEMPRE la lista de votaciones, incluso si hay una activa
          <div>
            <h4 className="mb-4 text-lg font-medium text-slate-900">
              Sesiones de Votación Creadas
            </h4>
            <div className="space-y-3">
              {votingSessions.map((session) => (
                <div key={session.id} className="rounded-xl border border-slate-200 bg-slate-50/40 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-slate-900">{session.question}</h5>
                      {session.description && (
                        <p className="mt-1 text-sm text-slate-600">{session.description}</p>
                          )}
                          <div className="mt-2 flex items-center space-x-4 text-sm text-slate-500">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              session.status === 'draft' 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : session.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : session.status === 'cancelled'
                                ? 'bg-red-100 text-red-800'
                                : session.status === 'completed'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-slate-100 text-slate-800'
                            }`}>
                              <span className="inline-flex items-center gap-1.5">
                                {session.status === 'draft' ? (
                                  <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                                ) : session.status === 'active' ? (
                                  <Circle className="h-3.5 w-3.5" aria-hidden="true" />
                                ) : session.status === 'cancelled' ? (
                                  <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                                ) : session.status === 'completed' ? (
                                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                                ) : (
                                  <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                                )}
                                <span>
                                  {session.status === 'draft' ? 'Borrador' : 
                                   session.status === 'active' ? 'Activa' : 
                                   session.status === 'cancelled' ? 'Cancelada' :
                                   session.status === 'completed' ? 'Completada' :
                                   session.status}
                                </span>
                              </span>
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <Clock className="h-4 w-4" aria-hidden="true" />
                              <span>{session.duration / 60} min</span>
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <BarChart3 className="h-4 w-4" aria-hidden="true" />
                              <span>{session.options.length} opciones</span>
                            </span>
                          </div>
                        </div>
                        {(session.status === 'draft' || session.status === 'active') && (
                          <div className="ml-4 flex space-x-2">
                            {session.status === 'draft' && (
                              <>
                                <button
                                  onClick={() => handleEditSession(session)}
                                  className="flex items-center space-x-1 rounded-lg bg-blue-600 px-3 py-2 text-white transition-colors hover:bg-blue-700"
                                  title="Editar votación"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                  <span>Editar</span>
                                </button>
                                <button
                                  onClick={() => handleDeleteSession(session.id, session.status)}
                                  className="flex items-center space-x-1 rounded-lg bg-red-600 px-3 py-2 text-white transition-colors hover:bg-red-700"
                                  title="Eliminar votación"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                  <span>Eliminar</span>
                                </button>
                                <button
                                  onClick={() => handleStartSession(session.id)}
                                  className={`flex items-center space-x-1 rounded-lg px-3 py-2 text-white transition-colors ${isAssemblyActive ? 'bg-green-600 hover:bg-green-700' : 'cursor-not-allowed bg-slate-400'}`}
                                  disabled={!isAssemblyActive}
                                >
                                  <PlayIcon className="h-4 w-4" />
                                  <span>Iniciar</span>
                                </button>
                              </>
                            )}
                            {session.status === 'active' && (
                              <button
                                onClick={() => handleEndVotingSession(session.id)}
                                className="flex items-center space-x-1 rounded-lg bg-orange-600 px-3 py-2 text-white transition-colors hover:bg-orange-700"
                                title="Terminar votación activa"
                              >
                                <StopIcon className="h-4 w-4" />
                                <span>Terminar</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <PlusIcon className="mx-auto mb-4 h-12 w-12 text-slate-400" />
                <h4 className="mb-2 text-lg font-medium text-slate-900">
                  Sin Votaciones Creadas
                </h4>
                <p className="text-slate-600">
                  No hay ninguna sesión de votación creada. Crea una nueva para comenzar.
                </p>
              </div>
            )}
      </div>
    </div>
  );
};

export default VotingSessionManager;